import { useMemo, type ReactNode } from "react";
import { Box, Tabs, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { CSSProperties } from "react";
import { PartnershipHandshakeIcon } from "@/components/icons/PartnershipHandshakeIcon";
import { IconBuilding, IconFileText, IconInfoCircle } from "@tabler/icons-react";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  NAV_TAB_LABEL_FZ,
  navTabsListStyles,
  navyNavTabSegmentBorders,
} from "@/shared/styles/navTabsLayout";

const CFC_TAB_TOOLTIP =
  "Partner Readiness is a short questionnaire designed to better understand your expectations for this collaboration. Based on your responses, it evaluates compatibility with your partner, provides a score, and highlights areas where you align as well as topics that may need further discussion.";

const COLLAB_MOBILE_TAB_ICON = 28;

export type CollaborationWorkspaceTabId = "overview" | "assessment" | "documents";

type CollaborationWorkspaceTabShellProps = {
  activeTab: string | null;
  onTabChange: (value: string | null) => void;
  overviewPanel: ReactNode;
  assessmentPanel: ReactNode;
  documentsPanel: ReactNode;
};

export default function CollaborationWorkspaceTabShell({
  activeTab,
  onTabChange,
  overviewPanel,
  assessmentPanel,
  documentsPanel,
}: CollaborationWorkspaceTabShellProps) {
  const theme = useMantineTheme();
  const isWorkspaceTabsMobile = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true }
  );

  const collaborationWorkspaceTabsListStyles = useMemo(
    () => ({
      list: isWorkspaceTabsMobile
        ? {
            display: "grid" as const,
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            alignItems: "stretch" as const,
            boxShadow: "none" as const,
            gap: 0,
            flexShrink: 0,
          }
        : {
            ...navTabsListStyles(3, { flexShrink: 0 }).list,
          },
    }),
    [isWorkspaceTabsMobile]
  );

  const collaborationWorkspaceTabStyles = useMemo(
    () => ({
      tab: {
        display: "flex" as const,
        alignItems: "center" as const,
        ...(isWorkspaceTabsMobile
          ? {
              flexDirection: "column" as const,
              justifyContent: "center" as const,
              alignItems: "center" as const,
              alignSelf: "stretch" as const,
              minHeight: 56,
              paddingTop: "var(--mantine-spacing-xs)",
              paddingBottom: "var(--mantine-spacing-xs)",
            }
          : {}),
      },
      tabLabel: {
        flex: 1,
        textAlign: "center" as const,
      },
      tabSection: {
        marginInlineStart: 0,
      },
    }),
    [isWorkspaceTabsMobile]
  );

  const tabIconColor = (tab: CollaborationWorkspaceTabId): CSSProperties => ({
    color: activeTab === tab ? "#ffffff" : "var(--mantine-color-slate-6)",
  });

  const interestTabStyle = (tab: CollaborationWorkspaceTabId) => ({
    backgroundColor: activeTab === tab ? "#04305c" : "#ebf0ff",
    color: activeTab === tab ? "white" : "var(--mantine-color-slate-6)",
    borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
    ...navyNavTabSegmentBorders(activeTab === tab),
    whiteSpace: "nowrap" as const,
    fontWeight: activeTab === tab ? 500 : 400,
    cursor: "pointer" as const,
    minHeight: isWorkspaceTabsMobile ? 56 : 44,
  });

  const tabRightSpacer = (
    <Box
      w={28}
      h={28}
      miw={28}
      mih={28}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
    />
  );

  return (
    <Tabs
      value={activeTab}
      onChange={onTabChange}
      style={{
        display: "flex",
        flexDirection: "column",
        ...(isWorkspaceTabsMobile ? {} : { flex: 1, minHeight: 0 }),
      }}
    >
      <Box w="100%" style={{ ...NAV_TABS_FULL_WIDTH_LINE_STYLE, overflow: "visible" }}>
        <Tabs.List grow={!!isWorkspaceTabsMobile} styles={collaborationWorkspaceTabsListStyles}>
          <Tabs.Tab
            value="overview"
            aria-label="Overview"
            px={{ base: 4, sm: "lg" }}
            py="xs"
            fz={NAV_TAB_LABEL_FZ}
            style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("overview") }}
            styles={collaborationWorkspaceTabStyles}
            leftSection={
              <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IconBuilding size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("overview")} />
              </Box>
            }
            rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
          >
            <Text
              component="span"
              fz={NAV_TAB_LABEL_FZ}
              visibleFrom="sm"
              c={activeTab === "overview" ? "#ffffff" : "var(--mantine-color-slate-6)"}
              fw={activeTab === "overview" ? 500 : 400}
            >
              Overview
            </Text>
          </Tabs.Tab>
          <Tabs.Tab
            value="assessment"
            aria-label="Assessment"
            px={{ base: 4, sm: "lg" }}
            py="xs"
            fz={NAV_TAB_LABEL_FZ}
            style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("assessment") }}
            styles={collaborationWorkspaceTabStyles}
            leftSection={
              <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PartnershipHandshakeIcon size={COLLAB_MOBILE_TAB_ICON} style={tabIconColor("assessment")} />
              </Box>
            }
            rightSection={
              <Box
                visibleFrom="sm"
                w={28}
                h={28}
                miw={28}
                mih={28}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Tooltip label={CFC_TAB_TOOLTIP} multiline maw={360} withArrow position="top">
                  <Box
                    component="span"
                    role="img"
                    aria-label="What is the Assessment?"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <IconInfoCircle
                      size={16}
                      stroke={1.5}
                      style={{
                        color: activeTab === "assessment" ? "#ffffff" : "var(--mantine-color-slate-6)",
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            }
          >
            <Text
              component="span"
              fz={NAV_TAB_LABEL_FZ}
              visibleFrom="sm"
              c={activeTab === "assessment" ? "#ffffff" : "var(--mantine-color-slate-6)"}
              fw={activeTab === "assessment" ? 500 : 400}
            >
              Assessment
            </Text>
          </Tabs.Tab>
          <Tabs.Tab
            value="documents"
            aria-label="Shared Documents"
            px={{ base: 4, sm: "lg" }}
            py="xs"
            fz={NAV_TAB_LABEL_FZ}
            style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("documents") }}
            styles={collaborationWorkspaceTabStyles}
            leftSection={
              <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IconFileText size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("documents")} />
              </Box>
            }
            rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
          >
            <Text
              component="span"
              fz={NAV_TAB_LABEL_FZ}
              visibleFrom="sm"
              c={activeTab === "documents" ? "#ffffff" : "var(--mantine-color-slate-6)"}
              fw={activeTab === "documents" ? 500 : 400}
            >
              Shared Documents
            </Text>
          </Tabs.Tab>
        </Tabs.List>
      </Box>

      <Tabs.Panel
        value="overview"
        pt={isWorkspaceTabsMobile ? "md" : "xl"}
        style={isWorkspaceTabsMobile ? undefined : { flex: 1, overflow: "auto" }}
      >
        {overviewPanel}
      </Tabs.Panel>
      <Tabs.Panel
        value="assessment"
        pt="md"
        style={
          isWorkspaceTabsMobile
            ? undefined
            : { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }
        }
      >
        {assessmentPanel}
      </Tabs.Panel>
      <Tabs.Panel
        value="documents"
        pt={isWorkspaceTabsMobile ? "md" : "xl"}
        style={isWorkspaceTabsMobile ? undefined : { flex: 1, overflow: "auto" }}
      >
        {documentsPanel}
      </Tabs.Panel>
    </Tabs>
  );
}
